import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  FolderOpen,
  ImagePlus,
  MoreHorizontal,
  PencilLine,
  Plus,
  Search,
  Video,
  ImageOff,
  Trash2,
} from 'lucide-react';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/errorMessage';
import LiquidCreateButton from '../components/LiquidCreateButton.tsx';
import PageTitleBlock from '../components/PageTitleBlock';
import ProjectDeleteDialog from '../components/ProjectDeleteDialog';
import './CanvasProjectsPage.css';
import NewCanvasDialog from './NewCanvasDialog';
import CanvasVideoCover from './CanvasVideoCover';
import { canvasListPath } from './canvasListNavigation';

const PREVIEW_CANVASES = [
  {
    id: 'preview-canvas',
    name: '雾港来信 · 第 12 集',
    created_at: '2026-08-01T09:30:00.000Z',
    preview_images: [
      { url: '/images/vnext/assets/su-zhaoying.jpg', type: 'character' },
      { url: '/images/vnext/assets/chen-qi.jpg', type: 'character' },
      { url: '/images/vnext/assets/xueluo-secret-room.jpg', type: 'scene' },
    ],
  },
  {
    id: 'preview-canvas-2',
    name: '新中式香氛广告',
    created_at: '2026-07-31T13:16:00.000Z',
    preview_images: [
      { url: '/images/vnext/featured-cases.jpg', type: 'output' },
      { url: '/images/vnext/canvas.jpg', type: 'scene' },
    ],
  },
  {
    id: 'preview-canvas-3',
    name: '宇航员紧急返航',
    created_at: '2026-07-30T08:45:00.000Z',
    preview_images: [
      { url: '/images/vnext/case-sci-fi-astronaut.jpg', type: 'character' },
    ],
  },
  {
    id: 'preview-canvas-4',
    name: '山海奇谈概念片',
    created_at: '2026-07-28T17:12:00.000Z',
    preview_images: [
      { url: '/images/vnext/case-fantasy-creature.jpg', type: 'character' },
      { url: '/images/vnext/case-guoman-3d.jpg', type: 'character' },
      { url: '/images/vnext/case-historical-romance.jpg', type: 'scene' },
      { url: '/images/vnext/assets/ye-changge.jpg', type: 'character' },
    ],
  },
];

function timeAgo(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return '最近创建';
  const seconds = Math.max(0, (Date.now() - timestamp) / 1000);
  if (seconds < 60) return '刚刚';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? '昨天' : `${days} 天前`;
}

function previewImages(canvas) {
  const raw = Array.isArray(canvas.preview_assets) && canvas.preview_assets.length
    ? canvas.preview_assets : canvas.preview_images;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => typeof item === 'string' ? { url: item } : item)
    .filter(item => item?.url)
    .slice(0, 4);
}

function CanvasPreview({ canvas }) {
  const [failed, setFailed] = useState([]);
  const keyFor = item => `${item.url}|${item.poster_url || item.thumbnail_url || item.cover_url || ''}`;
  const previews = previewImages(canvas);
  const available = previews.filter(item => !failed.includes(keyFor(item)));
  const video = available.find(item => item.type === 'video');
  const images = available.filter(item => item.type !== 'video');
  const fail = item => setFailed(current => [...current, keyFor(item)]);

  if (video) {
    return (
      <div className="canvas-project-preview is-1 has-video" aria-hidden="true">
        <CanvasVideoCover key={keyFor(video)} video={video} onError={() => fail(video)} />
        <div className="canvas-project-preview__video-label"><Video size={13} /><span>视频</span></div>
      </div>
    );
  }
  if (!images.length) {
    return (
      <div className="canvas-project-preview is-empty" aria-hidden="true">
        {previews.length
          ? <div className="canvas-project-preview__unavailable"><ImageOff size={23} /><small>封面暂不可用</small></div>
          : <><span /><span /><span /><i /><i /></>}
      </div>
    );
  }

  return (
    <div className={`canvas-project-preview is-${images.length}`} aria-hidden="true">
      {images.map((image, index) => (
        <img
          src={image.url}
          alt=""
          className={image.type === 'character' ? 'is-character' : ''}
          loading="lazy"
          decoding="async"
          onError={() => fail(image)}
          key={`${image.url}-${index}`}
        />
      ))}
    </div>
  );
}

function CanvasSort({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = [
    { value: 'recent', label: '最近创建' },
    { value: 'oldest', label: '最早创建' },
    { value: 'name', label: '按名称' },
  ];
  const active = options.find(option => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const close = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = event => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div className={`canvas-project-sort${open ? ' is-open' : ''}`} ref={rootRef}>
      <button type="button" aria-label="画布排序" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(current => !current)}>
        <span>{active.label}</span><ChevronDown size={15} />
      </button>
      {open && (
        <div role="listbox" aria-label="画布排序选项">
          {options.map(option => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? 'is-selected' : ''}
              onClick={() => { onChange(option.value); setOpen(false); }}
              key={option.value}
            >
              <span>{option.label}</span>{option.value === value && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CanvasCard({ canvas, onOpen, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = event => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const escape = event => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);

  return (
    <article className="canvas-project-card" data-canvas-id={canvas.id}>
      <button type="button" className="canvas-project-card-main" onClick={event => onOpen(canvas, event.currentTarget)} aria-label={`打开画布 ${canvas.name || '未命名画布'}`}>
        <CanvasPreview canvas={canvas} />
        <div className="canvas-project-card-copy">
          <h3>{canvas.name || '未命名画布'}</h3>
          <span>{timeAgo(canvas.updated_at || canvas.created_at)}</span>
        </div>
      </button>
      <div className="canvas-project-menu" ref={menuRef}>
        <button type="button" ref={menuButtonRef} aria-label={`管理 ${canvas.name || '未命名画布'}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(current => !current)}><MoreHorizontal size={18} /></button>
        {menuOpen && (
          <div role="menu">
            <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onRename(canvas); }}><PencilLine size={14} />重命名</button>
            <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuOpen(false); menuButtonRef.current?.focus(); onDelete(canvas); }}><Trash2 size={14} />删除画布</button>
          </div>
        )}
      </div>
    </article>
  );
}

export default function CanvasProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = useRef(null);
  const restoredLocation = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { preview = false } = useOutletContext() || {};
  const [canvases, setCanvases] = useState(preview ? PREVIEW_CANVASES : []);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(searchParams.get('create') === '1');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [notice, setNotice] = useState('');
  const deleteInFlight = useRef(false);
  const query = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || 'recent';
  const [searchInput, setSearchInput] = useState(query);
  const searchComposing = useRef(false);

  useLayoutEffect(() => {
    // A passive URL-sync effect can run after the next keystroke and move the caret.
    if (!searchComposing.current) setSearchInput(query);
  }, [query]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(timeout);
  }, [notice]);

  const loadCanvases = async () => {
    if (preview) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/canvas');
      setCanvases(Array.isArray(response.data) ? response.data : []);
    } catch {
      setError('画布暂时没有加载出来，但里面的节点没有被修改。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCanvases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const shown = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = canvases.filter(canvas => !normalized || String(canvas.name || '').toLowerCase().includes(normalized));
    return [...filtered].sort((a, b) => {
      if (sort === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sort === 'name') return String(a.name || '').localeCompare(String(b.name || ''), 'zh-CN');
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [canvases, query, sort]);

  useLayoutEffect(() => {
    const saved = location.state?.canvasListReturn;
    if (loading || error || !saved || restoredLocation.current === location.key
      || saved.path !== canvasListPath(preview, location.search)) return;
    const page = pageRef.current;
    const scroller = page?.closest('main') || document.scrollingElement;
    if (!scroller) return;
    const cards = Array.from(page.querySelectorAll('[data-canvas-id]'));
    const card = cards.find(item => item.dataset.canvasId === String(saved.canvasId))
      || (Number.isInteger(saved.index) && cards[Math.min(Math.max(saved.index, 0), cards.length - 1)]);
    if (card) {
      const offset = Number.isFinite(saved.offset) ? saved.offset : 24;
      const maxOffset = Math.max(16, scroller.clientHeight - card.clientHeight - 24);
      const visibleOffset = Math.max(16, Math.min(offset, maxOffset));
      scroller.scrollTop += card.getBoundingClientRect().top - scroller.getBoundingClientRect().top - visibleOffset;
      card.querySelector('.canvas-project-card-main')?.focus({ preventScroll: true });
    } else {
      scroller.scrollTop = Number.isFinite(saved.scrollTop) ? saved.scrollTop : 0;
    }
    restoredLocation.current = location.key;
  }, [error, loading, location, preview, shown]);

  const updateParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
  };

  const changeSearch = event => {
    const value = event.currentTarget.value;
    // Router updates run in a transition; keep the native IME value synchronous.
    setSearchInput(value);
    if (!searchComposing.current && !event.nativeEvent.isComposing) updateParam('q', value, '');
  };

  const finishSearchComposition = event => {
    searchComposing.current = false;
    const value = event.currentTarget.value;
    setSearchInput(value);
    updateParam('q', value, '');
  };

  const openCanvas = (canvas, button) => {
    const scroller = pageRef.current?.closest('main') || document.scrollingElement;
    const card = button?.closest('[data-canvas-id]');
    const target = {
      path: canvasListPath(preview, searchParams),
      canvasId: canvas.id,
      index: shown.findIndex(item => item.id === canvas.id),
      offset: card && scroller ? card.getBoundingClientRect().top - scroller.getBoundingClientRect().top : 24,
      scrollTop: scroller?.scrollTop || 0,
    };
    const state = { ...location.state, canvasListReturn: target };
    // Save both history entries: browser Back and the editor's list action use the same anchor.
    navigate(target.path, { replace: true, state });
    navigate(preview ? '/prototype/vnext/canvas/preview-canvas' : `/canvas/${canvas.id}`, { state });
  };

  const renameCanvas = async canvas => {
    const nextName = window.prompt('给这块画布换个名字', canvas.name || '未命名画布');
    const value = nextName?.trim();
    if (!value || value === canvas.name) return;
    if (preview) {
      setCanvases(current => current.map(item => item.id === canvas.id ? { ...item, name: value } : item));
      return;
    }
    try {
      await api.patch(`/canvas/${canvas.id}/name`, { name: value });
      setCanvases(current => current.map(item => item.id === canvas.id ? { ...item, name: value } : item));
    } catch {
      setError('名字暂时没有改成功，画布内容没有受到影响。');
    }
  };

  const requestDelete = canvas => {
    setNotice('');
    setDeleteError('');
    setDeleteTarget(canvas);
  };

  const closeDelete = () => {
    if (deleteInFlight.current) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteInFlight.current) return;
    if (preview) {
      setNotice('当前为界面预览，演示画布不会被删除。');
      setDeleteTarget(null);
      return;
    }
    deleteInFlight.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete(`/canvas/${deleteTarget.id}`);
      setCanvases(current => current.filter(canvas => canvas.id !== deleteTarget.id));
      setNotice(`已删除画布「${deleteTarget.name || '未命名画布'}」`);
      setDeleteTarget(null);
    } catch (requestError) {
      setDeleteError(getErrorMessage(requestError, '画布暂时没有删除成功，请稍后重试。'));
    } finally {
      deleteInFlight.current = false;
      setDeleting(false);
    }
  };

  return (
    <div className="canvas-projects-page vnext-page-surface" ref={pageRef}>
      <div className="canvas-projects-shell vnext-page-frame">
        <header className="canvas-projects-heading">
          <PageTitleBlock title="我的画布" />
          <LiquidCreateButton
            variant="lavender"
            label="新建画布"
            leadingIcon={<Plus size={17} />}
            showArrow={false}
            onClick={() => setNewOpen(true)}
          />
        </header>

        <section className="canvas-project-library" aria-label="我的画布列表">
          <div className="canvas-project-library-top">
            <div className="canvas-project-tools">
              <label>
                <Search size={15} />
                <input
                  value={searchInput}
                  onChange={changeSearch}
                  onCompositionStart={() => { searchComposing.current = true; }}
                  onCompositionEnd={finishSearchComposition}
                  placeholder="搜索画布"
                  aria-label="搜索画布"
                />
              </label>
              <CanvasSort value={sort} onChange={value => updateParam('sort', value, 'recent')} />
            </div>
          </div>

          {error && <div className="canvas-project-error" role="alert"><span>{error}</span><button type="button" onClick={loadCanvases}>重新加载</button></div>}

          {loading ? (
            <div className="canvas-project-grid" aria-label="正在加载画布">{[0, 1, 2, 3, 4, 5].map(item => <div className="canvas-project-skeleton" key={item} />)}</div>
          ) : shown.length ? (
            <div className="canvas-project-grid">
              {shown.map(canvas => <CanvasCard canvas={canvas} onOpen={openCanvas} onRename={renameCanvas} onDelete={requestDelete} key={canvas.id} />)}
            </div>
          ) : (
            <div className="canvas-project-empty">
              {query ? <FolderOpen size={26} /> : <ImagePlus size={26} />}
              <h3>{query ? `没找到「${query}」` : '片场还是空的'}</h3>
              <p>{query ? '换个名字找找，或者清除搜索。' : '先建一块画布，不需要一次想好整支片子。'}</p>
              <button type="button" onClick={() => query ? setSearchParams({}) : setNewOpen(true)}>{query ? '清除搜索' : '创建第一块画布'}</button>
            </div>
          )}
        </section>
      </div>

      {newOpen && <NewCanvasDialog preview={preview} initialCaseId={searchParams.get('case') || ''} onClose={() => { setNewOpen(false); loadCanvases(); }} onCreated={canvas => { setNewOpen(false); openCanvas(canvas); }} />}
      {notice && <div className="canvas-project-notice" role="status"><CheckCircle2 size={16} aria-hidden="true" /><span>{notice}</span></div>}
      <ProjectDeleteDialog
        open={Boolean(deleteTarget)}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="删除画布"
        deleting={deleting}
        pendingLabel="正在删除画布，请稍候…"
        error={deleteError}
      >
        确定删除画布「<strong>{deleteTarget?.name || '未命名画布'}</strong>」吗？删除后将无法再访问这块画布及其中的节点和生成内容。
      </ProjectDeleteDialog>
    </div>
  );
}
